/**
 * Comp `.app` — the simplified landing: a 240px rail plus exactly one working
 * surface. Selection lives in the URL (`?c=` conversation, `?d=` dashboard) so
 * a view is linkable and browser-back works.
 *
 * The page owns the "start a conversation" mutation because both the rail and
 * the conversation surface need it: everything a user clicks here ends up as a
 * sentence said to the agent.
 *
 * The rail is resizable by dragging its right edge. Width is pixels, not a percentage: a
 * navigation rail should keep its size when the window changes, unlike `SplitPane`, whose
 * proportional split is right for two content panes and wrong for a sidebar.
 */

const RAIL_WIDTH_KEY = 'simple.railWidth';
const RAIL_MIN = 200;
const RAIL_MAX = 460;
const RAIL_DEFAULT = 240;
/** The collapsed rail's width, the detailed sidebar's icon rail (`AppShell.tsx`). */
const RAIL_COLLAPSED = 72;

function clampRail(px: number): number {
  return Math.min(RAIL_MAX, Math.max(RAIL_MIN, px));
}
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { listConversationsQueryKey } from '../../generated/api/@tanstack/react-query.gen.js';
import { conversationsApi } from '../../services/api/conversations.api.js';
import { useActiveModule } from '../../hooks/useActiveModule.js';
import { useUIStore } from '../../store/index.js';
import { SimpleRail } from './SimpleRail.js';
import { SimpleConversation } from './SimpleConversation.js';
import { SimpleDashboardView } from './SimpleDashboardView.js';

export const SimpleLandingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeModuleId } = useActiveModule();
  const queryClient = useQueryClient();

  const selectedConversationId = searchParams.get('c');
  const selectedDashboardId = searchParams.get('d');

  const [railWidth, setRailWidth] = useState(RAIL_DEFAULT);
  const [dragging, setDragging] = useState(false);
  // The same saved preference as the detailed sidebar (`AppShell`): collapsing one collapses both.
  const railOpen = useUIStore((s) => s.sidebarOpen);
  const toggleRail = useUIStore((s) => s.toggleSidebar);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Restored after mount rather than in useState's initializer so server-side/first paint does not
  // read localStorage, and a corrupt value falls back to the default instead of throwing.
  useEffect(() => {
    const stored = Number(window.localStorage.getItem(RAIL_WIDTH_KEY));
    if (Number.isFinite(stored) && stored > 0) setRailWidth(clampRail(stored));
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      setRailWidth(clampRail(e.clientX - rect.left));
    };
    const onUp = () => setDragging(false);
    // `select-none` on the body stops the drag from highlighting the rail's text as it moves.
    document.body.classList.add('select-none');
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.classList.remove('select-none');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  // Persisted only once the drag ends — writing on every mousemove would hammer localStorage.
  useEffect(() => {
    if (dragging) return;
    window.localStorage.setItem(RAIL_WIDTH_KEY, String(railWidth));
  }, [dragging, railWidth]);

  // null = the conversation default, preselected in the picker and resolved by the server.
  const [runtimePolicyId, setRuntimePolicyId] = useState<number | null>(null);
  const startMutation = useMutation({
    mutationFn: ({ message, attachments }: { message: string; attachments: number[] }) =>
      conversationsApi.startFromMessage({
        message,
        ...(attachments.length > 0 ? { attachments } : {}),
        moduleId: activeModuleId,
        surface: 'simple',
        ...(runtimePolicyId !== null ? { runtimePolicyId } : {}),
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: listConversationsQueryKey() });
      setSearchParams({ c: String(res.conversation.id) });
    },
  });

  // The first message, with the files the person attached to it (upload ids): files alone start a conversation too.
  const startConversation = useCallback(
    (message: string, attachments: number[] = []) => startMutation.mutate({ message, attachments }),
    [startMutation],
  );

  const selectConversation = useCallback(
    (conversationId: string) => setSearchParams({ c: conversationId }),
    [setSearchParams],
  );
  const selectDashboard = useCallback((dashboardId: string) => setSearchParams({ d: dashboardId }), [setSearchParams]);
  const clearSelection = useCallback(() => setSearchParams({}), [setSearchParams]);

  return (
    <div
      ref={rootRef}
      className="relative grid h-screen bg-bg text-body"
      style={{ gridTemplateColumns: `${railOpen ? railWidth : RAIL_COLLAPSED}px minmax(0,1fr)` }}
    >
      <SimpleRail
        activeModuleId={activeModuleId}
        selectedConversationId={selectedConversationId}
        selectedDashboardId={selectedDashboardId}
        onNewConversation={clearSelection}
        onSelectConversation={selectConversation}
        onSelectDashboard={selectDashboard}
        onSay={startConversation}
        expanded={railOpen}
        onToggle={toggleRail}
      />

      {/*
        The drag handle. It sits on the seam rather than inside either pane so neither has to
        reserve space for it; `col-resize` plus a 5px target keeps it findable without a visible
        bar. Double-click restores the default, which is the usual escape hatch when a pane has
        been dragged somewhere useless. A collapsed rail has a fixed width, so no handle.
      */}
      {railOpen && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize conversation list"
          onMouseDown={() => setDragging(true)}
          onDoubleClick={() => setRailWidth(RAIL_DEFAULT)}
          style={{ left: railWidth - 2 }}
          className={`absolute top-0 z-20 h-full w-[5px] cursor-col-resize ${
            dragging ? 'bg-accent' : 'bg-transparent hover:bg-accent-muted'
          }`}
        />
      )}

      {selectedDashboardId ? (
        <SimpleDashboardView
          dashboardId={selectedDashboardId}
          onSay={startConversation}
          starting={startMutation.isPending}
        />
      ) : (
        <SimpleConversation
          activeModuleId={activeModuleId}
          conversationId={selectedConversationId}
          onStartConversation={startConversation}
          starting={startMutation.isPending}
          runtimePolicyId={runtimePolicyId}
          onRuntimePolicyChange={setRuntimePolicyId}
          onOpenConversation={selectConversation}
        />
      )}
    </div>
  );
};
